/**
 * IMAGE UTILITIES
 * 
 * Handles image file processing including:
 * - HEIC/HEIF to JPEG conversion (for iPhone photos)
 * - File type validation
 * - Image compression (optional)
 * 
 * Usage:
 *   import { processImageFile, ACCEPTED_IMAGE_TYPES } from '../utils/imageUtils';
 *   
 *   const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
 *     const file = e.target.files?.[0];
 *     if (!file) return;
 *     
 *     const processedFile = await processImageFile(file);
 *     // Now upload processedFile...
 *   };
 */

import heic2any from 'heic2any';

// Accepted image types for file inputs
// Include HEIC/HEIF for iPhone users
export const ACCEPTED_IMAGE_TYPES = 'image/*,.heic,.heif';

// More specific accept string if you want to be explicit
export const ACCEPTED_IMAGE_TYPES_EXPLICIT = 
  'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif';

/**
 * Check if a file is a HEIC/HEIF image
 */
export const isHeicFile = (file: File): boolean => {
  // Check by MIME type
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    return true;
  }
  
  // Also check by extension (some browsers don't set MIME type correctly)
  const extension = file.name.toLowerCase().split('.').pop();
  return extension === 'heic' || extension === 'heif';
};

/**
 * Convert a HEIC/HEIF file to JPEG
 * Returns the original file if it's not HEIC/HEIF or if already browser-readable
 */
export const convertHeicToJpeg = async (file: File): Promise<File> => {
  if (!isHeicFile(file)) {
    return file;
  }

  try {
    console.log(`Converting HEIC file: ${file.name}`);

    // Convert HEIC to JPEG blob
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9, // Good quality, reasonable file size
    });

    // heic2any can return a single blob or an array (for multi-image HEIC)
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

    // Create a new File object with .jpg extension
    const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    const convertedFile = new File([blob], newFileName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    console.log(`Converted ${file.name} (${formatFileSize(file.size)}) → ${newFileName} (${formatFileSize(convertedFile.size)})`);

    return convertedFile;
  } catch (error: unknown) {
    // heic2any throws an error if the file is already browser-readable
    // (e.g., a JPEG with .heic extension, or browser has native HEIC support)
    const errorObj = error as { code?: number; message?: string };
    if (errorObj?.code === 1 && errorObj?.message?.includes('already browser readable')) {
      console.log(`File ${file.name} is already browser-readable, skipping conversion`);
      // Return with corrected extension since it's actually a JPEG
      const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
      return new File([file], newFileName, {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
    }

    console.error('Error converting HEIC file:', error);
    throw new Error(`Failed to convert HEIC image: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Process an image file for upload
 * - Converts HEIC/HEIF to JPEG
 * - Validates file type
 * - Returns processed file ready for upload
 */
export const processImageFile = async (file: File): Promise<File> => {
  // Validate it's an image
  if (!file.type.startsWith('image/') && !isHeicFile(file)) {
    throw new Error('Please select an image file');
  }

  // Convert HEIC if needed
  if (isHeicFile(file)) {
    return await convertHeicToJpeg(file);
  }

  return file;
};

/**
 * Process multiple image files
 */
export const processImageFiles = async (files: FileList | File[]): Promise<File[]> => {
  const fileArray = Array.from(files);
  const processedFiles: File[] = [];

  for (const file of fileArray) {
    try {
      const processed = await processImageFile(file);
      processedFiles.push(processed);
    } catch (error) {
      console.error(`Error processing ${file.name}:`, error);
      // Continue with other files, or throw if you want to fail on any error
    }
  }

  return processedFiles;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Check if file size is within limit
 */
export const isFileSizeValid = (file: File, maxSizeMB: number = 10): boolean => {
  const maxBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxBytes;
};

/**
 * Validate image file
 * Returns error message or null if valid
 */
export const validateImageFile = (file: File, maxSizeMB: number = 10): string | null => {
  if (!file.type.startsWith('image/') && !isHeicFile(file)) {
    return 'Please select an image file';
  }

  if (!isFileSizeValid(file, maxSizeMB)) {
    return `File size must be under ${maxSizeMB}MB`;
  }

  return null;
};

/**
 * Compress an image file
 * - Converts HEIC/HEIF to JPEG first if needed
 * - Resizes to maxWidth while maintaining aspect ratio
 * - Compresses to JPEG with specified quality
 */
export const compressImage = async (
  file: File,
  maxWidth: number = 1200,
  quality: number = 0.8
): Promise<File> => {
  // Convert HEIC first if needed
  const convertedFile = await convertHeicToJpeg(file);

  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File(
              [blob],
              convertedFile.name.replace(/\.[^.]+$/, '.jpg'),
              { type: 'image/jpeg', lastModified: Date.now() }
            );
            URL.revokeObjectURL(img.src);
            resolve(compressedFile);
          } else {
            reject(new Error('Compression failed'));
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(convertedFile);
  });
};

/**
 * Compress an image blob (for URL imports)
 * - Resizes to maxWidth while maintaining aspect ratio
 * - Compresses to JPEG with specified quality
 * - Returns original blob if not an image or already small
 */
export const compressImageBlob = (
  blob: Blob,
  maxWidth: number = 1200,
  quality: number = 0.8
): Promise<Blob> => {
  return new Promise((resolve) => {
    // If not an image or already small, return as-is
    if (!blob.type.startsWith('image/') || blob.size < 100000) {
      resolve(blob);
      return;
    }

    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      let { width, height } = img;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (resultBlob) => {
          URL.revokeObjectURL(img.src);
          if (resultBlob) {
            resolve(resultBlob);
          } else {
            resolve(blob); // Fall back to original
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve(blob); // Fall back to original
    };

    img.src = URL.createObjectURL(blob);
  });
};
